(function () {
  'use strict';

  var ATTR = 'data-product-list-swiper';
  var instances = new WeakMap();
  var mobileLayoutQuery = window.matchMedia('(max-width: 767px)');

  function syncNavigationHeight(shell) {
    if (!shell) return;
    var figure = shell.querySelector('.idx-card__figure');
    if (!figure) return;
    var height = figure.getBoundingClientRect().height;
    if (height > 0) shell.style.setProperty('--product-list-media-height', height + 'px');
  }

  function getScrollProgress(element) {
    if (!element) return null;
    var maxScroll = Math.max(0, element.scrollWidth - element.clientWidth);
    if (maxScroll <= 0) return null;
    return Math.min(1, Math.max(0, Math.abs(element.scrollLeft) / maxScroll));
  }

  function getGeometryProgress(swiper, root) {
    if (!swiper || !root || !swiper.slides || !swiper.slides.length) return null;

    var firstSlide = swiper.slides[0];
    var lastSlide = swiper.slides[swiper.slides.length - 1];
    var rootRect = root.getBoundingClientRect();
    var firstRect = firstSlide.getBoundingClientRect();
    var lastRect = lastSlide.getBoundingClientRect();
    var contentWidth = Math.max(0, lastRect.right - firstRect.left);
    var maximum = Math.max(0, contentWidth - rootRect.width);
    if (maximum <= 1) return null;

    var offset = Math.max(0, rootRect.left - firstRect.left);
    return Math.min(1, Math.max(0, offset / maximum));
  }

  function getProductProgress(swiper, root, rail) {
    var geometryProgress = getGeometryProgress(swiper, root);
    if (geometryProgress !== null) return geometryProgress;

    var railProgress = getScrollProgress(rail);
    if (railProgress !== null && Math.abs(rail.scrollLeft) > 0.5) return railProgress;

    var rootProgress = getScrollProgress(root);
    if (rootProgress !== null && Math.abs(root.scrollLeft) > 0.5) return rootProgress;

    var swiperProgress = Number(swiper.progress);
    if (!Number.isFinite(swiperProgress)) swiperProgress = 0;
    return Math.min(1, Math.max(0, swiperProgress));
  }

  function syncProgress(swiper, root, rail, progress, fill) {
    if (!swiper || !progress || !fill || !mobileLayoutQuery.matches) return;

    var normalizedProgress = getProductProgress(swiper, root, rail);
    var slidesPerView = Number(swiper.params.slidesPerView) || 1;
    var totalSlides = swiper.slides && swiper.slides.length ? swiper.slides.length : 0;
    var initialRatio = totalSlides > 0 ? Math.min(1, slidesPerView / totalSlides) : 1;
    var value = Math.min(1, initialRatio + normalizedProgress * (1 - initialRatio));

    fill.style.transform = 'scaleX(' + value + ')';
    progress.setAttribute('aria-valuenow', String(Math.round(value * 100)));
  }

  function primeImage(image, highPriority) {
    if (!image || image.dataset.productListPrimed === 'true') return;
    image.dataset.productListPrimed = 'true';
    image.loading = 'eager';
    image.fetchPriority = highPriority ? 'high' : 'low';
    if (typeof image.decode === 'function') image.decode().catch(function () {});
  }

  function primeVisibleImages(swiper) {
    if (!swiper || !swiper.slides || !swiper.slides.length) return;

    var visibleCount = Math.ceil(Number(swiper.params.slidesPerView) || 1);
    var start = Math.max(0, swiper.activeIndex || 0);

    for (var offset = 0; offset < visibleCount; offset += 1) {
      var slide = swiper.slides[start + offset];
      if (!slide) break;
      primeImage(slide.querySelector('.idx-card__image'), true);
    }
  }

  function primeRemainingImages(swiper) {
    if (!swiper || !swiper.slides || !swiper.slides.length) return;

    var images = Array.prototype.map.call(swiper.slides, function (slide) {
      return slide.querySelector('.idx-card__image');
    }).filter(Boolean);
    var index = 0;

    function schedule(callback) {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(callback, { timeout: 1200 });
      } else {
        window.setTimeout(function () {
          callback(null);
        }, 650);
      }
    }

    function processBatch(deadline) {
      var processed = 0;
      while (index < images.length && processed < 2) {
        if (deadline && deadline.timeRemaining() < 4 && !deadline.didTimeout) break;
        primeImage(images[index], false);
        index += 1;
        processed += 1;
      }
      if (index < images.length) schedule(processBatch);
    }

    schedule(processBatch);
  }

  function bindDesktopInput(root, swiper) {
    var state = null;
    var suppressUntil = 0;
    var wheelTimer = 0;
    var controller = new AbortController();
    var options = { capture: true, signal: controller.signal };
    var interactive = 'button,input,select,textarea,[role="button"],.swiper-buttons,.swym-button';
    function desktop() { return !mobileLayoutQuery.matches && !swiper.destroyed; }
    function translate(value) {
      var bounded = Math.max(swiper.maxTranslate(), Math.min(swiper.minTranslate(), value));
      swiper.setTransition(0);
      swiper.setTranslate(bounded);
      swiper.updateProgress(bounded);
      swiper.updateActiveIndex();
      swiper.updateSlidesClasses();
      return bounded;
    }
    function finish(event) {
      if (!state || (event.pointerId != null && event.pointerId !== state.id)) return;
      var dragged = state.dragged;
      var id = state.id;
      state = null;
      root.classList.remove('is-desktop-dragging');
      if (root.hasPointerCapture(id)) root.releasePointerCapture(id);
      if (dragged) {
        suppressUntil = Date.now() + 500;
        swiper.slideToClosest(window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 220);
      }
    }
    root.addEventListener('pointerdown', function (event) {
      if (!desktop() || event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest(interactive)) return;
      suppressUntil = 0;
      clearTimeout(wheelTimer);
      state = { id: event.pointerId, x: event.clientX, y: event.clientY, start: swiper.getTranslate(), dragged: false };
    }, options);
    root.addEventListener('pointermove', function (event) {
      if (!state || event.pointerId !== state.id) return;
      if (!desktop() || event.buttons !== 1) { finish(event); return; }
      var dx = event.clientX - state.x;
      var dy = event.clientY - state.y;
      if (!state.dragged) {
        if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 7) { state = null; return; }
        if (Math.abs(dx) < 7) return;
        state.dragged = true;
        root.setPointerCapture(event.pointerId);
        root.classList.add('is-desktop-dragging');
      }
      event.preventDefault();
      event.stopPropagation();
      translate(state.start + dx * (swiper.rtlTranslate ? -1 : 1));
    }, { capture: true, passive: false, signal: controller.signal });
    root.addEventListener('pointerup', finish, options);
    root.addEventListener('pointercancel', finish, options);
    root.addEventListener('lostpointercapture', finish, options);
    root.addEventListener('pointerleave', function (event) { if (state && !state.dragged) finish(event); }, options);
    root.addEventListener('dragstart', function (event) { if (desktop() && state) event.preventDefault(); }, options);
    root.addEventListener('click', function (event) {
      if (desktop() && Date.now() < suppressUntil && !event.target.closest(interactive)) {
        event.preventDefault(); event.stopImmediatePropagation();
      }
    }, options);
    root.addEventListener('wheel', function (event) {
      if (!desktop() || event.ctrlKey || state || event.target.closest(interactive)) return;
      var delta = event.shiftKey && Math.abs(event.deltaX) < 1 ? event.deltaY : event.deltaX;
      if (!delta || (!event.shiftKey && Math.abs(event.deltaY) > Math.abs(delta))) return;
      delta *= event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? root.clientWidth : 1;
      var before = swiper.getTranslate();
      var next = Math.max(swiper.maxTranslate(), Math.min(swiper.minTranslate(), before - delta * (swiper.rtlTranslate ? -1 : 1)));
      if (Math.abs(next - before) < 0.1) return;
      event.preventDefault();
      translate(next);
      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(function () { if (desktop()) swiper.slideToClosest(180); }, 180);
    }, { passive: false, capture: true, signal: controller.signal });
    return function () { controller.abort(); clearTimeout(wheelTimer); root.classList.remove('is-desktop-dragging'); state = null; };
  }

  function init(root) {
    if (!root || instances.has(root) || typeof Swiper === 'undefined') return;

    var slideCount = root.querySelectorAll('.swiper-slide').length;
    if (slideCount === 0) return;

    var shell = root.closest('.product-list-shell');
    var navRoot = shell ? shell.querySelector('.swiper-buttons') : root.querySelector('.swiper-buttons');
    var prevEl = navRoot ? navRoot.querySelector('.swiper-button-prev') : null;
    var nextEl = navRoot ? navRoot.querySelector('.swiper-button-next') : null;
    var container = root.closest('.product-list-container');
    var progress = container ? container.querySelector('[data-product-list-progressbar]') : null;
    var progressFill = container ? container.querySelector('[data-product-list-progress]') : null;
    var progressRail = root.querySelector('.swiper-wrapper');

    var useNativeMobileScroll = mobileLayoutQuery.matches;
    var swiper = new Swiper(root, {
      cssMode: useNativeMobileScroll,
      slidesPerView: 2.2,
      spaceBetween: 12,
      simulateTouch: true,
      allowTouchMove: true,
      touchRatio: 1,
      touchAngle: 45,
      grabCursor: true,
      threshold: 5,
      touchStartPreventDefault: false,
      passiveListeners: true,
      watchSlidesProgress: false,
      preloadImages: false,
      breakpoints: {
        768: {
          slidesPerView: 4,
          simulateTouch: false,
          spaceBetween: 20,
        },
      },
      navigation:
        prevEl && nextEl
          ? {
              prevEl: prevEl,
              nextEl: nextEl,
            }
          : undefined,
      on: {
        init: function () {
          primeVisibleImages(this);
          primeRemainingImages(this);
        },
        breakpoint: function () {
          primeVisibleImages(this);
        },
      },
    });

    primeVisibleImages(swiper);
    syncNavigationHeight(shell);

    var progressFrame = 0;
    var progressTouchStartX = null;
    var progressWrapArmed = false;
    var refreshProgress = function () {
      if (progressFrame) return;
      progressFrame = requestAnimationFrame(function () {
        progressFrame = 0;
        syncProgress(swiper, root, progressRail, progress, progressFill);
      });
    };
    var onProgressTouchStart = function (event) {
      if (!mobileLayoutQuery.matches || !event.touches || !event.touches.length) return;
      progressTouchStartX = event.touches[0].clientX;
      progressWrapArmed = Boolean(swiper.isEnd) || getProductProgress(swiper, root, progressRail) >= 0.985;
    };
    var onProgressTouchEnd = function (event) {
      if (!mobileLayoutQuery.matches || progressTouchStartX === null) return;
      var changedTouch = event.changedTouches && event.changedTouches[0];
      var endX = changedTouch ? changedTouch.clientX : progressTouchStartX;
      var shouldWrap = progressWrapArmed && progressTouchStartX - endX > 36;
      progressTouchStartX = null;
      progressWrapArmed = false;

      if (!shouldWrap) {
        refreshProgress();
        return;
      }

      var speed = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 420;
      swiper.slideTo(0, speed);
      window.setTimeout(refreshProgress, speed + 40);
    };
    var onProgressTouchCancel = function () {
      progressTouchStartX = null;
      progressWrapArmed = false;
    };

    if (progressRail && progress && progressFill) {
      progressRail.addEventListener('scroll', refreshProgress, { passive: true });
      root.addEventListener('scroll', refreshProgress, { passive: true });
      root.addEventListener('touchstart', onProgressTouchStart, { passive: true, capture: true });
      root.addEventListener('touchmove', refreshProgress, { passive: true, capture: true });
      root.addEventListener('touchend', onProgressTouchEnd, { passive: true, capture: true });
      root.addEventListener('touchcancel', onProgressTouchCancel, { passive: true, capture: true });
      swiper.on('progress', refreshProgress);
      swiper.on('setTranslate', refreshProgress);
      swiper.on('sliderMove', refreshProgress);
      swiper.on('touchMove', refreshProgress);
      swiper.on('slideChange', refreshProgress);
      swiper.on('transitionEnd', refreshProgress);
      swiper.on('resize', refreshProgress);
      refreshProgress();
    }

    var resizeObserver = null;
    var resizeHandler = null;
    var figure = shell ? shell.querySelector('.idx-card__figure') : null;
    if (figure && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(function () {
        syncNavigationHeight(shell);
      });
      resizeObserver.observe(figure);
    } else {
      resizeHandler = function () {
        syncNavigationHeight(shell);
      };
      window.addEventListener('resize', resizeHandler);
    }

    var cleanupDesktopInput = bindDesktopInput(root, swiper);
    instances.set(root, {
      cleanupDesktopInput: cleanupDesktopInput,
      swiper: swiper,
      resizeObserver: resizeObserver,
      resizeHandler: resizeHandler,
      progressRoot: root,
      progressRail: progressRail,
      refreshProgress: refreshProgress,
      onProgressTouchStart: onProgressTouchStart,
      onProgressTouchEnd: onProgressTouchEnd,
      onProgressTouchCancel: onProgressTouchCancel,
      progressFrame: function () { return progressFrame; },
    });
  }

  function destroyIn(container) {
    (container || document).querySelectorAll('[' + ATTR + ']').forEach(function (root) {
      var instance = instances.get(root);
      if (!instance) return;
      if (instance.cleanupDesktopInput) instance.cleanupDesktopInput();
      if (instance.resizeObserver) instance.resizeObserver.disconnect();
      if (instance.resizeHandler) window.removeEventListener('resize', instance.resizeHandler);
      if (instance.progressRail && instance.refreshProgress) {
        instance.progressRail.removeEventListener('scroll', instance.refreshProgress);
      }
      if (instance.progressRoot && instance.refreshProgress) {
        instance.progressRoot.removeEventListener('scroll', instance.refreshProgress);
        instance.progressRoot.removeEventListener('touchmove', instance.refreshProgress, true);
      }
      if (instance.progressRoot && instance.onProgressTouchStart) {
        instance.progressRoot.removeEventListener('touchstart', instance.onProgressTouchStart, true);
      }
      if (instance.progressRoot && instance.onProgressTouchEnd) {
        instance.progressRoot.removeEventListener('touchend', instance.onProgressTouchEnd, true);
      }
      if (instance.progressRoot && instance.onProgressTouchCancel) {
        instance.progressRoot.removeEventListener('touchcancel', instance.onProgressTouchCancel, true);
      }
      var progressFrame = instance.progressFrame ? instance.progressFrame() : 0;
      if (progressFrame) cancelAnimationFrame(progressFrame);
      instance.swiper.destroy(true, true);
      instances.delete(root);
    });
  }

  function initAll(container) {
    (container || document).querySelectorAll('[' + ATTR + ']').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event && event.target) initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (event && event.target) destroyIn(event.target);
  });
})();

